import { computed, getCurrentScope, onScopeDispose, ref, watch } from 'vue';

import {
  emptyPaginationMeta,
  mergeUniqueRecords,
  normalizePaginationMeta,
} from './agentResourceHelpers';

const timestampValue = value => {
  if (!value) return 0;
  if (Number.isFinite(Number(value))) return Number(value);
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed / 1000;
};

export const buildRecentActivityItems = ({
  runs,
  memories,
  approvals,
  assistant,
  translate,
  hasTranslation,
}) => {
  const translatedEnum = (namespace, value, fallback = '') => {
    const key = `CAPTAIN_EXPERIENCE.${namespace}.${String(value || '').toUpperCase()}`;
    return hasTranslation(key) ? translate(key) : fallback || value;
  };

  return [
    ...runs.map(run => ({
      title: translatedEnum('RUN_STATUS', run.status, run.status_label),
      description:
        run.response ||
        run.error_message ||
        translate('CAPTAIN_EXPERIENCE.SCRIPT.NO_RESPONSE_RECORDED'),
      actor_name:
        run.current_agent_name ||
        assistant?.name ||
        translate('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_ASSISTANT_NAME'),
      occurred_at: run.updated_at || run.created_at,
    })),
    ...memories.map(memory => ({
      title: translate('CAPTAIN_EXPERIENCE.SCRIPT.MEMORY_SAVED_TITLE', {
        type: translatedEnum(
          'MEMORY_TYPE',
          memory.memory_type,
          memory.type_label
        ),
      }),
      description: memory.content,
      actor_name: translate('CAPTAIN_EXPERIENCE.SCRIPT.SYSTEM_ACTOR'),
      occurred_at: memory.updated_at || memory.created_at,
    })),
    ...approvals.map(approval => ({
      title: translatedEnum(
        'APPROVAL_STATUS',
        approval.status,
        approval.status_label
      ),
      description:
        approval.reason ||
        approval.requested_action ||
        translate('CAPTAIN_EXPERIENCE.SCRIPT.APPROVAL_ACTIVITY_FALLBACK'),
      actor_name:
        approval.audit_summary?.actor_name ||
        translate('CAPTAIN_EXPERIENCE.SCRIPT.SYSTEM_ACTOR'),
      occurred_at: approval.decided_at || approval.created_at,
    })),
  ]
    .filter(Boolean)
    .sort(
      (current, next) =>
        timestampValue(next.occurred_at) - timestampValue(current.occurred_at)
    )
    .slice(0, 5);
};

export const useAgentActivity = ({
  assistantId,
  canManage,
  approvalsEnabled,
  toolsEnabled,
  api,
  notify,
  t,
  resourceErrors,
  readResourceError,
}) => {
  const actions = ref([]);
  const runs = ref([]);
  const runsMeta = ref(emptyPaginationMeta());
  const approvals = ref([]);
  const approvalsMeta = ref(emptyPaginationMeta());
  const isLoadingMoreRuns = ref(false);
  const isCancellingRun = ref(false);
  const runPendingCancel = ref(null);
  const selectedRunDetail = ref(null);
  const loadingRunId = ref(null);
  let actionsRequestId = 0;
  let runsRequestId = 0;
  let approvalsRequestId = 0;
  let detailRequestId = 0;
  let mutationRequestId = 0;

  const availableActions = computed(() =>
    actions.value.filter(action => action.enabled !== false)
  );
  const hasMoreRuns = computed(
    () => Number(runsMeta.value.page) < Number(runsMeta.value.total_pages)
  );
  const pendingApprovalsCount = computed(
    () => Number(approvalsMeta.value.total_count) || 0
  );
  const completedRunsCount = computed(
    () => Number(runsMeta.value.status_counts?.completed) || 0
  );
  const failedRunsCount = computed(
    () => Number(runsMeta.value.status_counts?.failed) || 0
  );
  const handoffRunsCount = computed(
    () => Number(runsMeta.value.handoff_count) || 0
  );

  const isCurrent = (requestId, currentRequestId, targetAssistantId) =>
    requestId === currentRequestId && targetAssistantId === assistantId.value;

  const fetchActions = async (targetAssistantId = assistantId.value) => {
    actionsRequestId += 1;
    const currentRequestId = actionsRequestId;
    if (!targetAssistantId || !toolsEnabled.value || !canManage.value) {
      if (isCurrent(actionsRequestId, currentRequestId, targetAssistantId)) {
        actions.value = [];
      }
      return [];
    }

    const { data } = await api.actions({ assistantId: targetAssistantId });
    const records = data.payload || [];
    if (isCurrent(actionsRequestId, currentRequestId, targetAssistantId)) {
      actions.value = records;
    }
    return records;
  };

  const fetchRuns = async ({
    targetAssistantId = assistantId.value,
    page = 1,
    append = false,
  } = {}) => {
    runsRequestId += 1;
    const currentRequestId = runsRequestId;
    if (!targetAssistantId || !approvalsEnabled.value || !canManage.value) {
      if (isCurrent(runsRequestId, currentRequestId, targetAssistantId)) {
        runs.value = [];
        runsMeta.value = emptyPaginationMeta();
      }
      return [];
    }

    if (append) isLoadingMoreRuns.value = true;
    try {
      const { data } = await api.runs({
        assistantId: targetAssistantId,
        page,
        perPage: 25,
      });
      const records = data.payload || [];
      if (!isCurrent(runsRequestId, currentRequestId, targetAssistantId)) {
        return records;
      }

      runs.value = append ? mergeUniqueRecords(runs.value, records) : records;
      runsMeta.value = normalizePaginationMeta(data.meta, {
        page,
        per_page: 25,
      });
      return records;
    } finally {
      if (isCurrent(runsRequestId, currentRequestId, targetAssistantId)) {
        isLoadingMoreRuns.value = false;
      }
    }
  };

  const fetchApprovals = async ({
    targetAssistantId = assistantId.value,
    page = 1,
    append = false,
  } = {}) => {
    approvalsRequestId += 1;
    const currentRequestId = approvalsRequestId;
    if (!targetAssistantId || !approvalsEnabled.value || !canManage.value) {
      if (isCurrent(approvalsRequestId, currentRequestId, targetAssistantId)) {
        approvals.value = [];
        approvalsMeta.value = emptyPaginationMeta();
      }
      return [];
    }

    const { data } = await api.approvals({
      assistantId: targetAssistantId,
      status: 'pending',
      page,
      perPage: 25,
    });
    const records = data.payload || [];
    if (!isCurrent(approvalsRequestId, currentRequestId, targetAssistantId)) {
      return records;
    }

    approvals.value = append
      ? mergeUniqueRecords(approvals.value, records)
      : records;
    approvalsMeta.value = normalizePaginationMeta(data.meta, {
      page,
      per_page: 25,
    });
    return records;
  };

  const loadMoreRuns = async () => {
    if (!hasMoreRuns.value || isLoadingMoreRuns.value) return;
    const targetAssistantId = assistantId.value;
    const expectedRequestId = runsRequestId + 1;
    try {
      await fetchRuns({
        targetAssistantId,
        page: Number(runsMeta.value.page) + 1,
        append: true,
      });
      if (isCurrent(runsRequestId, expectedRequestId, targetAssistantId)) {
        resourceErrors.runs = '';
      }
    } catch (error) {
      if (isCurrent(runsRequestId, expectedRequestId, targetAssistantId)) {
        resourceErrors.runs = readResourceError(error);
      }
    }
  };

  const requestCancelRun = run => {
    if (canManage.value) runPendingCancel.value = run;
  };

  const cancelRunRequest = () => {
    if (!isCancellingRun.value) runPendingCancel.value = null;
  };

  const toggleRunDetails = async run => {
    if (!canManage.value) return;
    if (selectedRunDetail.value?.id === run.id) {
      detailRequestId += 1;
      selectedRunDetail.value = null;
      loadingRunId.value = null;
      return;
    }

    const targetAssistantId = assistantId.value;
    detailRequestId += 1;
    const currentRequestId = detailRequestId;
    loadingRunId.value = run.id;
    try {
      const { data } = await api.run({
        assistantId: targetAssistantId,
        runId: run.id,
      });
      const detail = data.payload || {};
      if (!isCurrent(detailRequestId, currentRequestId, targetAssistantId)) {
        return;
      }
      const [replayPlan, handoffPlan] = await Promise.allSettled([
        api.replayPlan({ assistantId: targetAssistantId, runId: run.id }),
        api.handoffPlan({ assistantId: targetAssistantId, runId: run.id }),
      ]);
      if (isCurrent(detailRequestId, currentRequestId, targetAssistantId)) {
        selectedRunDetail.value = {
          ...detail,
          replay_plan:
            replayPlan.status === 'fulfilled'
              ? replayPlan.value?.data?.payload || null
              : null,
          replay_error:
            replayPlan.status === 'rejected'
              ? readResourceError(replayPlan.reason)
              : '',
          handoff_plan:
            handoffPlan.status === 'fulfilled'
              ? handoffPlan.value?.data?.payload || null
              : null,
          handoff_error:
            handoffPlan.status === 'rejected'
              ? readResourceError(handoffPlan.reason)
              : '',
        };
      }
    } catch (error) {
      if (!isCurrent(detailRequestId, currentRequestId, targetAssistantId)) {
        return;
      }
      notify(
        error?.message || t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_LOAD_RUN_ERROR')
      );
    } finally {
      if (isCurrent(detailRequestId, currentRequestId, targetAssistantId)) {
        loadingRunId.value = null;
      }
    }
  };

  const cancelRun = async () => {
    if (!canManage.value || !runPendingCancel.value) return false;

    const targetAssistantId = assistantId.value;
    const targetRun = runPendingCancel.value;
    mutationRequestId += 1;
    const currentRequestId = mutationRequestId;
    isCancellingRun.value = true;
    try {
      await api.cancelRun({
        assistantId: targetAssistantId,
        runId: targetRun.id,
      });
      if (!isCurrent(mutationRequestId, currentRequestId, targetAssistantId)) {
        return false;
      }

      runPendingCancel.value = null;
      await Promise.all([
        fetchRuns({ targetAssistantId }),
        fetchApprovals({ targetAssistantId }),
      ]);
      if (!isCurrent(mutationRequestId, currentRequestId, targetAssistantId)) {
        return false;
      }

      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_RUN_CANCELLED'));
      return true;
    } catch (error) {
      if (!isCurrent(mutationRequestId, currentRequestId, targetAssistantId)) {
        return false;
      }
      notify(
        error?.message || t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_CANCEL_RUN_ERROR')
      );
      return false;
    } finally {
      if (isCurrent(mutationRequestId, currentRequestId, targetAssistantId)) {
        isCancellingRun.value = false;
      }
    }
  };

  const reset = () => {
    actionsRequestId += 1;
    runsRequestId += 1;
    approvalsRequestId += 1;
    detailRequestId += 1;
    mutationRequestId += 1;
    actions.value = [];
    runs.value = [];
    runsMeta.value = emptyPaginationMeta();
    approvals.value = [];
    approvalsMeta.value = emptyPaginationMeta();
    isLoadingMoreRuns.value = false;
    isCancellingRun.value = false;
    runPendingCancel.value = null;
    selectedRunDetail.value = null;
    loadingRunId.value = null;
  };

  const stopAssistantWatch = watch(assistantId, reset);
  const dispose = () => {
    stopAssistantWatch();
    reset();
  };
  if (getCurrentScope()) onScopeDispose(dispose);

  return {
    actions,
    availableActions,
    runs,
    runsMeta,
    approvals,
    approvalsMeta,
    hasMoreRuns,
    pendingApprovalsCount,
    completedRunsCount,
    failedRunsCount,
    handoffRunsCount,
    isLoadingMoreRuns,
    isCancellingRun,
    runPendingCancel,
    selectedRunDetail,
    loadingRunId,
    fetchActions,
    fetchRuns,
    fetchApprovals,
    loadMoreRuns,
    requestCancelRun,
    cancelRunRequest,
    toggleRunDetails,
    cancelRun,
    reset,
    dispose,
  };
};
